import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { User, LogOut, Settings, Shield, Compass, Ticket, MessageCircle, Gift } from "lucide-react";
import LanguageSelector from "./LanguageSelector";

const Navigation = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [adminUnread, setAdminUnread] = useState(0);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Poll unread counts for support tickets
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setAdminUnread(0);
      return;
    }
    const API = process.env.REACT_APP_BACKEND_URL;
    const fetchCounts = async () => {
      try {
        const r = await fetch(`${API}/api/support/unread-count`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          setUnreadCount(d.unread_count || 0);
        }
        if (user.role === "admin") {
          const r2 = await fetch(`${API}/api/admin/support/unread-count`, { credentials: "include" });
          if (r2.ok) {
            const d2 = await r2.json();
            setAdminUnread(d2.unread_count || 0);
          }
        }
      } catch (e) {
        // silent
      }
    };
    fetchCounts();
    const iv = setInterval(fetchCounts, 30000);
    return () => clearInterval(iv);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    setDropdownOpen(false);
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-lg border-b border-border">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className="font-heading text-sm text-foreground hover:text-gold transition-colors"
              data-testid="nav-link-home"
            >
              {t("nav.home")}
            </Link>
            <Link 
              to="/store" 
              className="font-heading text-sm text-foreground hover:text-gold transition-colors"
              data-testid="nav-link-store"
            >
              {t("nav.store")}
            </Link>
            <Link 
              to="/about" 
              className="font-heading text-sm text-foreground hover:text-gold transition-colors"
              data-testid="nav-link-about"
            >
              {t("nav.about")}
            </Link>
            <Link 
              to="/contact" 
              className="font-heading text-sm text-foreground hover:text-gold transition-colors"
              data-testid="nav-link-contact"
            >
              {t("nav.contact")}
            </Link>
            <Link 
              to="/affiliati" 
              className="font-heading text-sm text-foreground hover:text-gold transition-colors"
              data-testid="nav-link-partners"
            >
              {t("nav.partners")}
            </Link>
          </div>
          
          {/* Auth Buttons */}
          <div className="flex items-center space-x-4">
            <LanguageSelector variant="ghost" />
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="relative flex items-center space-x-2 px-4 py-2 bg-card border border-gold/50 hover:border-gold rounded-lg transition-all"
                  data-testid="nav-user-button"
                >
                  {(unreadCount > 0 || adminUnread > 0) && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-ruby rounded-full ring-2 ring-background" data-testid="nav-notification-dot"></span>
                  )}
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                  ) : (
                    <User className="w-4 h-4 text-gold" />
                  )}
                  <span className="font-heading text-sm text-gold">{user.name}</span>
                  <svg
                    className={`w-4 h-4 text-gold transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="font-heading text-sm text-foreground">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    
                    <div className="py-2">
                      <Link
                        to="/dashboard"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-gold/10 transition-colors"
                      >
                        <User className="w-4 h-4 mr-3 text-gold" />
                        {t("nav.dashboard")}
                      </Link>
                      
                      <Link
                        to="/dashboard#webapps"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-gold/10 transition-colors"
                      >
                        <Compass className="w-4 h-4 mr-3 text-gold" />
                        {t("nav.myApps")}
                      </Link>
                      
                      <Link
                        to="/redeem"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-gold/10 transition-colors"
                      >
                        <Ticket className="w-4 h-4 mr-3 text-gold" />
                        {t("nav.redeem")}
                      </Link>

                      <Link
                        to="/contact"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center justify-between px-4 py-2 text-sm text-foreground hover:bg-gold/10 transition-colors"
                        data-testid="nav-my-tickets"
                      >
                        <span className="flex items-center">
                          <MessageCircle className="w-4 h-4 mr-3 text-gold" />
                          {t("nav.myTickets")}
                        </span>
                        {unreadCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-ruby text-white text-xs font-heading" data-testid="nav-unread-badge">
                            {unreadCount}
                          </span>
                        )}
                      </Link>

                      <Link
                        to="https://ko-fi.com/mytharion"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center justify-between px-4 py-2 text-sm text-foreground hover:bg-gold/10 transition-colors"
                      >
                        <span className="flex items-center">
                          <Gift className="w-4 h-4 mr-3 text-gold" />
                          {t("nav.donate")}
                        </span>
                      </Link>

                      {user.role === "admin" && (
                        <Link
                          to="/admin"
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center justify-between px-4 py-2 text-sm text-foreground hover:bg-ruby/10 transition-colors"
                        >
                          <span className="flex items-center">
                            <Shield className="w-4 h-4 mr-3 text-ruby" />
                            {t("nav.adminPanel")}
                          </span>
                          {adminUnread > 0 && (
                            <span className="px-2 py-0.5 rounded-full bg-ruby text-white text-xs font-heading" data-testid="nav-admin-unread-badge">
                              {adminUnread}
                            </span>
                          )}
                        </Link>
                      )}
                      
                      <div className="border-t border-border my-2"></div>
                      
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-ruby hover:bg-ruby/10 transition-colors"
                        data-testid="nav-logout"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        {t("nav.logout")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="font-heading text-sm text-foreground hover:text-gold transition-colors"
                  data-testid="nav-login"
                >
                  {t("nav.login")}
                </Link>
                <Link 
                  to="/register" 
                  className="px-4 py-2 bg-gradient-to-b from-yellow-600 to-yellow-800 hover:from-yellow-500 hover:to-yellow-700 text-black font-heading text-sm rounded-lg gold-glow transition-all"
                  data-testid="nav-register"
                >
                  {t("nav.register")}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
